import { useState } from 'react';
import { useTradingPositionListPositions } from '@/lib/endpoints';
import {
  PlaeePositionResponseDtoSide,
  PlaeePositionResponseDtoStatus,
  TradingPositionListPositionsStatus,
} from '@/lib/models';
import type { PlaeePositionResponseDto } from '@/lib/models';
import { useCapabilityEnabled, KILL_SWITCH } from '@/lib/launchdarkly/killSwitches';
import { resolvePositionLeg } from '@/lib/trading/position';
import { legAnswerLabel } from '@/lib/trading/position-label';
import { fmtDateTime, fmtUsd, signedUsd } from '@/lib/trading/format';
import { cn } from '@/lib/utils';
import { CrownButton } from '@/components/ui';
import { CollapsibleSection } from './CollapsibleSection';
import { SellPopup, type SellPositionContext } from './SellPopup';
import { MarketOutcomePricesSubscriber, targetCents, useOutcomePrices } from './useOutcomePrices';
import type { MarketOptionDto } from '@/lib/models';

export interface MarketOpenPositionsProps {
  /** The event's tradeable options — positions are scoped to their outcomes. */
  options: MarketOptionDto[];
  /** Section title (admin-ordered). */
  title: string;
  /** Initial expanded state. */
  defaultOpen?: boolean;
  /** Expand/collapse analytics. */
  onToggle?: (open: boolean) => void;
}

/** Visual sentiment of a row — drives tint, text colour and the Sell button. */
type RowTone = 'gain' | 'loss' | 'neutral';

/** The action affordance on the right of a row. */
type RowAction = 'sell' | 'sold' | 'crown';

/** A single rendered position row, decoupled from the wire DTO. */
interface RowView {
  id: string;
  tone: RowTone;
  /** Leading coloured figure, e.g. `+$1` / `+1`. */
  pnlLabel: string;
  /** Mid label, e.g. `Value 70¢`. */
  valueLabel: string;
  /** Entry date + time, e.g. `Dec 31, 3:00 PM`. */
  time: string;
  action: RowAction;
  onSell?: () => void;
}

const TONE_TINT: Record<RowTone, string> = {
  gain: 'bg-success/10',
  loss: 'bg-danger/10',
  neutral: 'bg-accent-dark/10',
};
// Only the crown (neutral) row is outlined — a faint purple stroke (figma node 1782:53948).
const TONE_BORDER: Record<RowTone, string> = {
  gain: 'border border-transparent',
  loss: 'border border-transparent',
  neutral: 'border-2 border-accent-dark/20',
};
const TONE_TEXT: Record<RowTone, string> = {
  gain: 'text-success',
  loss: 'text-danger-alt',
  neutral: 'text-accent-dark',
};
const TONE_BUTTON: Record<RowTone, string> = {
  gain: 'from-success to-success-dark',
  loss: 'from-danger to-danger-dark',
  neutral: 'from-accent to-accent-dark',
};

/**
 * Open Positions section for the binary market page (ARC-82 §5.4.1).
 *
 * Lists the authenticated customer's open positions in **this event** — the
 * account-wide position mirror filtered to the event's market outcomes (one
 * cheap read, works for single- and multi-market events). Each row's **Sell**
 * opens the {@link SellPopup} sheet, where the user picks a quantity and
 * confirms the market sell (the sheet owns the gates + failure handling).
 */
export function MarketOpenPositions({ options, title, defaultOpen, onToggle }: MarketOpenPositionsProps) {
  const tradingEnabled = useCapabilityEnabled(KILL_SWITCH.TRADING_ORDER_PLACEMENT);
  const [selling, setSelling] = useState<SellPositionContext | null>(null);
  const { prices, setPrice } = useOutcomePrices();

  // External-outcome → human label, for scoping the account-wide positions to
  // this event and naming each row.
  const labelByOutcome = new Map<string, string>();
  for (const o of options) {
    const label = o.label ?? 'Position';
    labelByOutcome.set(o.yes.externalOutcomeId, label);
    labelByOutcome.set(o.no.externalOutcomeId, label);
  }

  // Distinct market channels to subscribe for the live-price overlay.
  const channels = new Map<string, string>();
  for (const o of options) channels.set(o.marketId, o.externalMarketId);

  // Scope the fetch to this event's market(s) server-side (instead of pulling
  // every position the user holds account-wide and filtering in the browser).
  const marketIds = [...new Set(options.map((o) => o.externalMarketId))].join(',');
  const { data: positions = [] } = useTradingPositionListPositions(
    { status: TradingPositionListPositionsStatus.open, marketIds },
    { query: { enabled: Boolean(marketIds) } },
  );
  const open = positions.filter(
    (p) => p.status === PlaeePositionResponseDtoStatus.OPEN && p.quantity > 0 && labelByOutcome.has(p.outcomeId),
  );

  // Project a netted position to its Yes/No identity + LIVE value and P&L.
  // Value and P&L track the realtime mark price (Centrifugo `price_updated`):
  //   value = qty × live price · P&L = qty × (live price − entry price).
  // The entry price comes from the API (Plaee's avgEntryPrice, or derived from our
  // fills server-side when Plaee's is still pending); it's `null` only while a fill
  // is still settling, in which case P&L falls back to the wire snapshot.
  function project(p: PlaeePositionResponseDto): { isYes: boolean; value: number; pnl: number } {
    const leg = resolvePositionLeg(p.outcomeId, p.side, options);
    const isYes = leg ? leg.betSide === 'yes' : p.side === PlaeePositionResponseDtoSide.LONG;
    const cents = leg ? targetCents(leg.target, prices) : null;
    if (cents == null) return { isYes, value: p.cost ?? 0, pnl: p.unrealizedPnl ?? 0 };
    const value = (p.quantity * cents) / 100;
    const entry = p.avgEntryPrice;
    const pnl = entry != null && entry > 0 ? value - p.quantity * entry : (p.unrealizedPnl ?? 0);
    return { isYes, value, pnl };
  }

  const rows = open.map((p) => ({ position: p, ...project(p) }));
  const totalPnl = rows.reduce((sum, r) => sum + r.pnl, 0);

  // Open the sell sheet for a position — quantity choice, gates (onboarding +
  // territory, per the sibling buy/limit sheets) and submission live there.
  function handleSell(p: PlaeePositionResponseDto) {
    if (!tradingEnabled) return;
    const leg = resolvePositionLeg(p.outcomeId, p.side, options);
    if (!leg) return; // not on this event's options — row shouldn't exist
    setSelling({ position: p, leg, label: legAnswerLabel(leg) });
  }

  // Label each row with its option name only when there's more than one option.
  const showName = options.length > 1;

  // Live rows projected to the figma row view-model.
  const displayRows: RowView[] = rows.map(({ position: p, isYes, value, pnl }) => ({
    id: p.positionId,
    tone: pnl >= 0 ? 'gain' : 'loss',
    pnlLabel: signedUsd(pnl),
    valueLabel: showName
      ? `${labelByOutcome.get(p.outcomeId)} ${fmtUsd(value)}`
      : `${isYes ? 'Yes' : 'No'} ${fmtUsd(value)}`,
    time: fmtDateTime(p.openedAt),
    action: 'sell',
    onSell: () => handleSell(p),
  }));

  const displayTotal = totalPnl;

  return (
    <CollapsibleSection
      title={`${displayRows.length} ${title}`}
      defaultOpen={defaultOpen}
      onToggle={onToggle}
      disabled={displayRows.length === 0}
      summary={
        displayRows.length > 0 ? (
          <span
            className={`rounded-pill px-2.5 py-0.5 text-sm font-medium ${
              displayTotal >= 0 ? 'bg-success/10 text-success' : 'bg-danger/10 text-danger-alt'
            }`}
          >
            Value {signedUsd(displayTotal)}
          </span>
        ) : (
          <span className="text-muted">No open positions</span>
        )
      }
    >
      <div className="flex flex-col gap-2">
        {[...channels].map(([marketId, externalMarketId]) => (
          <MarketOutcomePricesSubscriber
            key={marketId}
            marketId={marketId}
            externalMarketId={externalMarketId}
            onPrice={setPrice}
          />
        ))}

        {/* Column headers — Price · Value · Time (figma node 2071:76473). */}
        <div className="flex items-center pl-3.5 pr-1 text-[13px] text-muted/70">
          <span className="min-w-16 shrink-0 pr-2">Price</span>
          <span className="flex-1">Value</span>
          <span className="w-24 shrink-0 text-center">Time</span>
          <span className="ml-2 w-[88px] shrink-0" />
        </div>
        <div className="-mt-1 h-px bg-border" />

        {displayRows.map((row) => (
          <PositionRow key={row.id} row={row} disabled={!tradingEnabled} />
        ))}
      </div>

      <SellPopup sell={selling} onClose={() => setSelling(null)} />
    </CollapsibleSection>
  );
}

/**
 * One open-position row (figma node 1659:67561): a tinted pill carrying the
 * coloured P&L, value and entry time, with a Sell button / `Sold` label /
 * crown badge on the right depending on the row's `action`.
 */
function PositionRow({ row, disabled }: { row: RowView; disabled: boolean }) {
  const { tone, pnlLabel, valueLabel, time, action, onSell } = row;
  return (
    <div className={cn('relative flex h-7 items-center rounded-pill pl-3.5 pr-0', TONE_TINT[tone], TONE_BORDER[tone])}>
      <span className={cn('min-w-16 shrink-0 whitespace-nowrap pr-2 text-[13px] font-medium tabular-nums', TONE_TEXT[tone])}>
        {pnlLabel}
      </span>
      <span className="flex-1 truncate text-[13px] font-medium text-primary">{valueLabel}</span>
      <span className="w-24 shrink-0 whitespace-nowrap text-center text-xs text-primary/80">{time}</span>
      <div className="ml-2 flex w-[88px] shrink-0 items-center justify-end">
        {action === 'sell' && (
          <button
            type="button"
            onClick={onSell}
            disabled={disabled}
            className={cn(
              'h-7 w-[88px] rounded-pill bg-gradient-to-b text-[14px] font-semibold text-white shadow-inset-glow transition-opacity disabled:cursor-not-allowed disabled:opacity-50',
              TONE_BUTTON[tone],
            )}
          >
            Sell
          </button>
        )}
        {action === 'sold' && <span className={cn('pr-3 text-[15px] font-medium capitalize', TONE_TEXT[tone])}>Sold</span>}
        {action === 'crown' && (
          <div className="origin-right scale-[0.72]">
            <div className="bg-accent-tint rounded-pill w-[110px]">
              <CrownButton noBorder compact className="px-2" />
            </div>
          </div>
        )}
      </div>
    </div>
  );
}
